import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, firstValueFrom, race, timer } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import saveAs from 'file-saver';

import { AppState } from '../../../../state/app.state';
import { selectSidenavDataByTabIdAndKey } from '../../../../state/selectors';

import { InnovationMetricsActivityComponent, SecondaryAxis } from '../result-sections/company-view/innovation-metrics-activity/innovation-metrics-activity.component';
import { InnovationActivityOverTimeComponent, ScoreSecondaryAxis } from '../result-sections/company-view/innovation-activity-over-time/innovation-activity-over-time.component';

// Company types that get the Signal History section (mirrors sidebar gating).
const PUBLIC_TYPES = ['Public Company', 'Public Investment Firm'];

// Both chart components load this key; backend builds every other section itself.
const READY_KEYS = ['innov_metrics_history'];

@Component({
  selector: 'app-company-pdf-report',
  standalone: true,
  imports: [
    CommonModule,
    InnovationMetricsActivityComponent,
    InnovationActivityOverTimeComponent,
  ],
  templateUrl: './company-pdf-report.component.html',
  styleUrl: './company-pdf-report.component.css',
})
export class CompanyPdfReportComponent implements AfterViewInit {
  @Input() tabId!: string;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;
  @Input() gsubind: string[] | null = null;
  @Input() query: string | null = null;
  @Input() entityData: any = null;

  @ViewChildren(InnovationMetricsActivityComponent) private signalCharts?: QueryList<InnovationMetricsActivityComponent>;
  @ViewChildren(InnovationActivityOverTimeComponent) private activityChartCmps?: QueryList<InnovationActivityOverTimeComponent>;

  /** Emits this host's tabId when generation finishes (success or failure) so the parent can unmount it. */
  @Output() done = new EventEmitter<string>();

  readonly ACTIVITY_AXES: Exclude<ScoreSecondaryAxis, null>[] = ['abs_growth', 'growth', 'size', 'quality', 'rank'];
  readonly SIGNAL_AXES: SecondaryAxis[] = ['innovation_score'];
  readonly today = new Date('2026-06-18').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  constructor(private store: Store<AppState>, private http: HttpClient) {}

  /** Self-driving: capture this tab's charts, POST for the PDF, save it, then signal done. */
  async ngAfterViewInit(): Promise<void> {
    const entityid = this.entityids?.[0];
    if (!entityid) { this.done.emit(this.tabId); return; }
    const name = (this.tab_name?.trim() || 'Company').replace(/[\\/:*?"<>|]+/g, '');
    try {
      await this.whenReady();
      const charts = this.getChartImages();
      const blob = await firstValueFrom(this.http.post(
        `/backend/innovationMetrics/report/${entityid}/pdf`,
        { charts },
        {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('current_user_token') },
          responseType: 'blob',
        },
      ));
      saveAs(blob, `${name} - Innovation Report.pdf`);
    } catch (e) {
      console.error('Report PDF failed', e);
    } finally {
      this.done.emit(this.tabId);
    }
  }

  get isPublic(): boolean {
    return PUBLIC_TYPES.includes(this.entityData?.companytype);
  }

  /** Resolves when all expected sections have data in the store and charts have painted. */
  async whenReady(): Promise<void> {
    const allLoaded = combineLatest(
      READY_KEYS.map(k => this.store.select(selectSidenavDataByTabIdAndKey(this.tabId, k))),
    ).pipe(
      map(vals => vals.every(v => v != null)),
      filter(Boolean),
      take(1),
    );
    // Never hang: cap the wait at 20s even if a section errors/never loads.
    await firstValueFrom(race(allLoaded, timer(20000)));
    await this.settle();
  }

  /** Two animation frames + a delay so Chart.js finishes its draw animation. */
  private settle(): Promise<void> {
    return new Promise(res =>
      requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(res, 1500))),
    );
  }

  /** Collect exact Chart.js images keyed for the backend report (signal_history + activity_<axis>). */
  getChartImages(): Record<string, string> {
    const out: Record<string, string> = {};
    this.signalCharts?.forEach(c => {
      const img = c.getChartImage();
      if (c.lockedAxis && img) out['signal_' + c.lockedAxis] = img;
    });
    this.activityChartCmps?.forEach(c => {
      const img = c.getChartImage();
      if (c.lockedAxis && img) out['activity_' + c.lockedAxis] = img;
    });
    return out;
  }
}
